import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowUpRight, RefreshCw } from 'lucide-react'
import clsx from 'clsx'

import PaytrieFullIcon from '@/assets/paytrie-full-rgb.svg'
import { useMaintenanceWindows } from '@/hooks/useMaintenanceWindows'
import { useNow } from '@/hooks/useNow'
import { WidgetLayout } from '@/pages/Buy/WidgetLayout'
import { MaintenanceNotice } from './components/MaintenanceNotice'
import { PastWindows } from './components/PastWindows'
import { StatusCard } from './components/StatusCard'
import {
  CARD_CLASS_NAME,
  MUTED_TEXT_CLASS_NAME,
  SECTION_TITLE_CLASS_NAME,
} from './components/styles'
import { HISTORY_DAYS, partitionWindows } from './maintenanceTimeline'
import { useMaintenanceFormatters } from './useMaintenanceFormatters'

const CLOCK_TICK_MS = 30 * 1000
const FOOTER_LINK_CLASS_NAME =
  'font-medium text-gravel underline underline-offset-2 dark:text-snow'
const CONTACT_URL = 'https://paytrie.com/contact'
const API_DOCS_URL =
  'https://docs.paytrie.com/v2/api-reference/maintenance-windows/listMaintenanceWindows'

const LoadingState = memo(function LoadingState() {
  return (
    <div className="space-y-5" aria-busy="true">
      <div className="h-14 animate-pulse rounded-md bg-cloudDarker dark:bg-darkCharcoal" />
      <div className="h-32 animate-pulse rounded-md bg-cloudDarker dark:bg-darkCharcoal" />
      <div className="h-64 animate-pulse rounded-md bg-cloudDarker dark:bg-darkCharcoal" />
    </div>
  )
})

type ErrorStateProps = {
  onRetry: () => void
}

const ErrorState = memo(function ErrorState({ onRetry }: ErrorStateProps) {
  const { t } = useTranslation()
  return (
    <div
      className={clsx(
        CARD_CLASS_NAME,
        'flex flex-col items-center gap-3 p-8 text-center'
      )}
    >
      <p className="font-medium text-gravel dark:text-snow">
        {t('maintenance.error.title')}
      </p>
      <p className={clsx('text-sm', MUTED_TEXT_CLASS_NAME)}>
        {t('maintenance.error.description')}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 rounded-full bg-gravel px-4 py-2 text-sm font-medium text-snow hover:bg-pebble dark:bg-snow dark:text-gravel dark:hover:bg-cloud"
      >
        <RefreshCw className="h-4 w-4" aria-hidden />
        {t('maintenance.error.retry')}
      </button>
    </div>
  )
})

export const MaintenanceStatusPage = memo(function MaintenanceStatusPage() {
  const { t } = useTranslation()
  const { data, isPending, isError, refetch } = useMaintenanceWindows({
    lookbackDays: HISTORY_DAYS,
  })
  const handleRetry = useCallback(() => refetch(), [refetch])
  const now = useNow(CLOCK_TICK_MS)
  const formatters = useMaintenanceFormatters()

  const windows = useMemo(() => data ?? [], [data])
  const { inProgress, upcoming, past } = useMemo(
    () => partitionWindows({ windows, now }),
    [windows, now]
  )

  return (
    <WidgetLayout showPoweredByLabel={false} heightClassName="min-h-[100dvh]">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={PaytrieFullIcon}
            alt={t('topbar.logo.alt')}
            className="h-9 dark:invert"
          />
          <span className="h-6 w-px bg-gravel/15 dark:bg-snow/20" />
          <h1 className="text-lg font-semibold text-gravel dark:text-snow">
            {t('maintenance.page.title')}
          </h1>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-1 rounded-full border border-cloudDarker px-3 py-1.5 text-sm font-medium text-gravel hover:bg-cloudDarker dark:border-darkCharcoal dark:text-snow dark:hover:bg-darkCharcoal"
        >
          {t('maintenance.page.openApp')}
          <ArrowUpRight className="h-4 w-4" aria-hidden />
        </Link>
      </header>

      {isPending ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState onRetry={handleRetry} />
      ) : (
        <div className="space-y-8">
          <StatusCard
            windows={windows}
            currentWindow={inProgress[0]}
            now={now}
            formatters={formatters}
          />

          {inProgress.length > 0 && (
            <section className="space-y-3">
              <h2 className={SECTION_TITLE_CLASS_NAME}>
                {t('maintenance.current.title', { count: inProgress.length })}
              </h2>
              {inProgress.map((window) => (
                <MaintenanceNotice
                  key={window.id}
                  window={window}
                  now={now}
                  formatters={formatters}
                />
              ))}
            </section>
          )}

          {upcoming.length > 0 && (
            <section className="space-y-3">
              <h2 className={SECTION_TITLE_CLASS_NAME}>
                {t('maintenance.upcoming.title')}
              </h2>
              {upcoming.map((window) => (
                <MaintenanceNotice
                  key={window.id}
                  window={window}
                  now={now}
                  formatters={formatters}
                />
              ))}
            </section>
          )}

          <PastWindows windows={past} now={now} formatters={formatters} />
        </div>
      )}

      <footer
        className={clsx(
          'mt-4 space-y-1 text-center text-xs',
          MUTED_TEXT_CLASS_NAME
        )}
      >
        <p>
          {t('maintenance.page.api')}{' '}
          <a
            href={API_DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className={FOOTER_LINK_CLASS_NAME}
          >
            GET /v2/maintenance-windows
          </a>
        </p>
        <p>
          {t('maintenance.page.support')}{' '}
          <a
            href={CONTACT_URL}
            target="_blank"
            rel="noreferrer"
            className={FOOTER_LINK_CLASS_NAME}
          >
            {t('maintenance.page.contact')}
          </a>
        </p>
      </footer>
    </WidgetLayout>
  )
})
